import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contentAPI } from '../../api/contentApi';
import { useFormState, useApiRequest } from '../../hooks/useApiRequest';
import { ArrowLeftIcon, CheckIcon, ArrowUpOnSquareIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import ChatGPTIntegration from './ChatGPTIntegration';

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, error, makeRequest } = useApiRequest();
  
  const { formData, updateField, updateMultipleFields, resetForm } = useFormState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    tags: '',
    status: 'draft',
    meta_title: '',
    meta_description: ''
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [chatgptSettings, setChatgptSettings] = useState(null);
  const [availableSettings, setAvailableSettings] = useState([]);

  // toHtml kaldırıldı; backend HTML üretir

  // HTML içinden SEO alanlarını çıkar
  const extractSeoFieldsFromHtml = (html) => {
    if (!html) return {};
    const container = document.createElement('div');
    container.innerHTML = html;

    const result = {};
    // H2 başlığı bul, sonrasındaki p etiketlerini oku
    const headings = Array.from(container.querySelectorAll('h2'));
    const seoHeader = headings.find(h => h.textContent && h.textContent.toLowerCase().includes('özet ve seo bilgileri'));
    let pTags = [];
    if (seoHeader) {
      // seoHeader'dan sonraki kardeş p'leri topla (ilk farklı blok gelene kadar)
      let el = seoHeader.nextElementSibling;
      while (el) {
        if (el.tagName && el.tagName.toLowerCase() === 'p') {
          pTags.push(el);
          el = el.nextElementSibling;
        } else {
          break;
        }
      }
    } else {
      // Yine de tüm p'lerde ara
      pTags = Array.from(container.querySelectorAll('p'));
    }

    pTags.forEach(p => {
      const strong = p.querySelector('strong');
      const label = strong ? strong.textContent.trim().replace(':', '').toLowerCase() : '';
      const value = strong ? p.textContent.replace(strong.textContent, '').trim().replace(/^[:\s-]+/, '') : p.textContent.trim();
      if (!label || !value) return;
      if (label.startsWith('başlık')) result.title = value;
      else if (label.startsWith('özet')) result.excerpt = value;
      else if (label.startsWith('kategori')) result.category = value;
      else if (label.startsWith('etiket')) result.tags = value;
      else if (label.startsWith('meta başlık')) result.meta_title = value;
      else if (label.startsWith('meta açıklama')) result.meta_description = value;
    });

    return result;
  };

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      loadBlogPost(id);
    } else {
      setIsEditMode(false);
      resetForm();
      // no-op for image preview
    }
    loadChatGPTSettings();
  }, [id]);

  const loadChatGPTSettings = async () => {
    try {
      // Önce tüm aktif ayarları getir
      const activeResponse = await makeRequest(() => contentAPI.getActiveChatGPTSettings());
      
      if (activeResponse && activeResponse.length > 0) {
        setAvailableSettings(activeResponse);
        
        // Varsayılan ayarı bul
        const defaultSetting = activeResponse.find(setting => setting.is_default);
        if (defaultSetting) {
          setChatgptSettings(defaultSetting);
        } else {
          // Varsayılan yoksa ilk ayarı seç
          setChatgptSettings(activeResponse[0]);
        }
      }
    } catch (error) {
      // no-op
    }
  };

  const loadBlogPost = async (postId) => {
    try {
      const response = await makeRequest(() => contentAPI.getBlogPostById(postId));
      // DRF detail genellikle doğrudan obje döndürür
      const postData = (response && response.success && response.data) ? response.data : response;
      if (postData && postData.id) {
        updateMultipleFields({
          title: postData.title || '',
          content: postData.content || '',
          excerpt: postData.excerpt || '',
          category: postData.category || '',
          tags: postData.tags || '',
          status: postData.status || 'draft',
          meta_title: postData.meta_title || '',
          meta_description: postData.meta_description || ''
        });
      }
    } catch (err) {
      alert("Blog yazısı yüklenirken hata oluştu.");
    }
  };

  // removed image change handler

  const handleContentGenerated = (data) => {
    // Yeni JSON formatını kontrol et
    if (data.title) {
      // Yeni format: tüm veriler ayrı ayrı
      updateMultipleFields({
        title: data.title,
        content: data.content_html || data.content || '',
        excerpt: data.excerpt,
        category: data.category,
        tags: data.tags,
        meta_title: data.meta_title,
        meta_description: data.meta_description
      });
    } else {
      // Eski format: HTML string gelebilir → içeriği yaz ve SEO alanlarını ayıkla
      const html = typeof data === 'string' ? data : '';
      updateField('content', html);
      const extracted = extractSeoFieldsFromHtml(html);
      if (Object.keys(extracted).length > 0) {
        updateMultipleFields({
          title: extracted.title !== undefined ? extracted.title : formData.title,
          excerpt: extracted.excerpt !== undefined ? extracted.excerpt : formData.excerpt,
          category: extracted.category !== undefined ? extracted.category : formData.category,
          tags: extracted.tags !== undefined ? extracted.tags : formData.tags,
          meta_title: extracted.meta_title !== undefined ? extracted.meta_title : formData.meta_title,
          meta_description: extracted.meta_description !== undefined ? extracted.meta_description : formData.meta_description
        });
      }
    }
  };

  const handleSubmit = async (e, newStatus = null) => {
    e.preventDefault();
    
    try {
      // Basit doğrulamalar – backend'e boş veri göndermeyelim
      const titleValue = (formData.title || '').trim();
      const contentValue = (formData.content || '').trim();
      if (!titleValue) {
        alert('Başlık alanı boş bırakılamaz. Lütfen bir başlık girin.');
        return;
      }
      if (!contentValue) {
        alert('İçerik alanı boş bırakılamaz. Lütfen içerik girin.');
        return;
      }
      let response;
      
      if (isEditMode) {
        // Düzenleme modunda PATCH kullan
        const dataToSubmit = {
          title: titleValue,
          content: contentValue,
          excerpt: formData.excerpt,
          category: formData.category,
          tags: formData.tags,
          meta_title: formData.meta_title,
          meta_description: formData.meta_description
        };
        
        if (newStatus) {
          dataToSubmit.status = newStatus;
        }
        
        response = await makeRequest(() => contentAPI.partialUpdateBlogPost(id, dataToSubmit));
      } else {
        // Yeni oluşturma modunda FormData kullan
        const dataToSubmit = new FormData();
        
        // Metin alanları
        dataToSubmit.append('title', titleValue);
        dataToSubmit.append('content', contentValue);
        dataToSubmit.append('excerpt', formData.excerpt || '');
        dataToSubmit.append('category', formData.category || '');
        dataToSubmit.append('tags', formData.tags || '');
        dataToSubmit.append('meta_title', formData.meta_title || '');
        dataToSubmit.append('meta_description', formData.meta_description || '');
        
        if (newStatus) {
          dataToSubmit.append('status', newStatus);
        }
        
        // no image upload
        
        response = await makeRequest(() => contentAPI.createBlogPost(dataToSubmit));
      }

      if (response && (response.success || response.id)) {
        alert(`Blog yazısı başarıyla ${isEditMode ? 'güncellendi' : 'oluşturuldu'}!`);
        navigate('/content-management');
      } else {
        const errMsg = response && (response.message || response.detail) ? (response.message || response.detail) : 'Bilinmeyen hata';
        alert(`Blog yazısı ${isEditMode ? 'güncellenirken' : 'oluşturulurken'} hata oluştu: ${errMsg}`);
      }
    } catch (err) {
      // Silently handle error
      alert("Blog yazısı kaydedilirken hata oluştu.");
    }
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate('/content-management')}
          className="flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" /> Geri
        </button>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {isEditMode ? 'Blog Yazısını Düzenle' : 'Yeni Blog Yazısı Oluştur'}
        </h2>
        <div className="flex space-x-2">
          {isEditMode && formData.status !== 'archived' && (
            <button
              onClick={(e) => handleSubmit(e, 'archived')}
              className="flex items-center px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors"
              disabled={loading}
            >
              <ArchiveBoxIcon className="w-5 h-5 mr-2" /> Arşivle
            </button>
          )}
          {formData.status !== 'published' && (
            <button
              onClick={(e) => handleSubmit(e, 'published')}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              disabled={loading}
            >
              <ArrowUpOnSquareIcon className="w-5 h-5 mr-2" /> Yayınla
            </button>
          )}
          <button
            onClick={handleSubmit}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            disabled={loading}
          >
            <CheckIcon className="w-5 h-5 mr-2" /> {isEditMode ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </div>

      {error && <p className="text-red-500 dark:text-red-300 mb-4">Hata: {error}</p>}
      {loading && <p className="text-blue-500 dark:text-blue-300 mb-4">Kaydediliyor...</p>}

      {/* ChatGPT Ayarları Seçimi */}
      {availableSettings.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              ChatGPT Ayarı
            </h3>
            <button
              type="button"
              onClick={() => navigate('/content-management/rules')}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              Ayarları Yönet
            </button>
          </div>
          
          <div className="space-y-2">
            <select
              value={chatgptSettings?.id || ''}
              onChange={(e) => {
                const selectedSetting = availableSettings.find(s => s.id === parseInt(e.target.value));
                setChatgptSettings(selectedSetting);
              }}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white text-sm"
            >
              {availableSettings.map((setting) => (
                <option key={setting.id} value={setting.id}>
                  {setting.name} ({setting.model_display}) {setting.is_default ? '- Varsayılan' : ''}
                </option>
              ))}
            </select>
            
            {chatgptSettings && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Max Tokens: {chatgptSettings.max_tokens} (~{Math.round(chatgptSettings.max_tokens * 0.6)} kelime) | 
                Temperature: {chatgptSettings.temperature} | 
                Top P: {chatgptSettings.top_p}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ChatGPT Integration - Her zaman göster, ayar yoksa uyarı içinde */}
      <ChatGPTIntegration onContentGenerated={handleContentGenerated} chatgptSettings={chatgptSettings} />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Başlık</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
            className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-white"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">İçerik (Yayına Hazır)</label>
          <div
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => updateField('content', e.currentTarget.innerHTML)}
            className="mt-1 block w-full min-h-[240px] p-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-white prose dark:prose-invert max-w-none focus:outline-none content-html"
            dangerouslySetInnerHTML={{ __html: formData.content || '' }}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Bu alan HTML olarak düzenlenebilir ve kaydedilir.</p>
        </div>

        <div>
          <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Özet (Opsiyonel)</label>
          <textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={(e) => updateField('excerpt', e.target.value)}
            rows="3"
            className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-white"
          ></textarea>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Kategori</label>
            <input
              type="text"
              id="category"
              name="category"
              value={formData.category}
              onChange={(e) => updateField('category', e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Etiketler (virgülle ayırın)</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={(e) => updateField('tags', e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {/* Öne çıkan görsel alanı kaldırıldı */}

        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">SEO Ayarları (Opsiyonel)</h3>
          <div>
            <label htmlFor="meta_title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Meta Başlık</label>
            <input
              type="text"
              id="meta_title"
              name="meta_title"
              value={formData.meta_title}
              onChange={(e) => updateField('meta_title', e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="mt-4">
            <label htmlFor="meta_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Meta Açıklama</label>
            <textarea
              id="meta_description"
              name="meta_description"
              value={formData.meta_description}
              onChange={(e) => updateField('meta_description', e.target.value)}
              rows="3"
              className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-white"
            ></textarea>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BlogEditor;